"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { LayoutDashboard, Settings, Trophy, MessageSquare, LifeBuoy, LogOut, type LucideIcon } from "lucide-react";
import { createClient } from "@/lib/supabase/client";

const MENU: { href: string; label: string; icon: LucideIcon }[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/settings", label: "Account settings", icon: Settings },
  { href: "/leaderboard/toefl", label: "Leaderboard", icon: Trophy },
  { href: "/feedback", label: "Send feedback", icon: MessageSquare },
  { href: "/support", label: "Get support", icon: LifeBuoy },
];

export function ProfileDropdown({
  email,
  displayName,
}: {
  email: string;
  displayName?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Initials for avatar
  const initials = (displayName ?? email)
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join("");

  async function signOut() {
    const supabase = createClient();
    await supabase.auth.signOut();
    window.location.href = "/";
  }

  const itemClass =
    "group flex items-center gap-3 mx-1.5 px-2.5 py-2 rounded-lg text-[13.5px] text-gray-700 hover:bg-gray-100/80 hover:text-gray-900 focus:outline-none focus-visible:bg-gray-100/80 transition-colors";
  const iconClass = "w-4 h-4 text-gray-400 group-hover:text-gray-700 transition-colors";

  return (
    <div className="relative" ref={ref}>
      {/* Avatar button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 rounded-full hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* Avatar uses a fixed indigo so the contrast against initials is
            stable across themes — previously `bg-[var(--text-primary)]` flipped
            to near-white in dark mode, rendering the white initials invisible. */}
        <div className="w-8 h-8 rounded-full bg-indigo-brand text-white text-xs font-bold flex items-center justify-center select-none">
          {initials || "?"}
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 w-60 bg-white rounded-xl shadow-xl ring-1 ring-gray-900/[0.06] py-1.5 z-50"
        >
          {/* Identity */}
          <div className="flex items-center gap-3 px-4 pt-2 pb-3 mb-1.5 border-b border-gray-100">
            <div className="w-9 h-9 shrink-0 rounded-full bg-blue-600/10 text-blue-600 text-xs font-bold flex items-center justify-center select-none">
              {initials || "?"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {displayName ?? "My account"}
              </div>
              <div className="text-xs text-gray-500 truncate">{email}</div>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-col gap-0.5">
            {MENU.map(({ href, label, icon: Icon }) => (
              <Link key={href} href={href} role="menuitem" onClick={() => setOpen(false)} className={itemClass}>
                <Icon className={iconClass} strokeWidth={1.75} aria-hidden="true" />
                <span>{label}</span>
              </Link>
            ))}
          </div>

          {/* Sign out */}
          <div className="mt-1.5 pt-1.5 border-t border-gray-100">
            <button type="button" role="menuitem" onClick={signOut} className={`${itemClass} w-[calc(100%-0.75rem)] text-left`}>
              <LogOut className={iconClass} strokeWidth={1.75} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
